/**
 * Tax Analysis Types and Interfaces
 *
 * Comprehensive type definitions for real estate investment tax analysis,
 * depreciation calculations, and tax reporting functionality.
 */

// User Tax Profile Configuration
export interface UserTaxProfile {
  user_id: string;
  filing_status: 'single' | 'married_joint' | 'married_separate' | 'head_of_household';
  federal_tax_bracket: number; // Marginal tax rate (e.g., 0.22 for 22%)
  state: string;
  state_tax_rate: number;
  depreciation_method: 'straight_line' | 'accelerated' | 'bonus';
  material_participation: boolean; // For passive activity loss rules
  agi_estimate: number; // Adjusted Gross Income estimate
  created_at: string;
  updated_at: string;
}

// Depreciation Methods and Schedules
export interface DepreciationSchedule {
  property_id: string;
  depreciable_basis: number;
  depreciation_method: 'straight_line' | 'accelerated';
  useful_life_years: number; // Typically 27.5 for residential
  land_value: number; // Non-depreciable portion
  start_date: string;
  annual_schedule: DepreciationYear[];
  total_depreciation: number;
  remaining_basis: number;
}

export interface DepreciationYear {
  year: number;
  calendar_year: string;
  depreciation_amount: number;
  accumulated_depreciation: number;
  remaining_basis: number;
  bonus_depreciation?: number; // For bonus depreciation years
}

// Tax Deductions and Benefits
export interface TaxDeductions {
  property_id: string;
  year: string;
  mortgage_interest: number;
  property_taxes: number;
  insurance: number;
  repairs_maintenance: number;
  professional_services: number; // Legal, accounting, property management
  depreciation: number;
  utilities: number;
  advertising: number;
  travel_expenses: number;
  office_expenses: number;
  other_expenses: OtherExpense[];
  total_deductions: number;
}

export interface OtherExpense {
  description: string;
  amount: number;
  category: string;
}

// Tax Analysis Results
export interface TaxAnalysisResult {
  property_id: string;
  analysis_year: string;
  rental_income: number;
  total_deductions: number;
  net_rental_income: number; // Before depreciation
  taxable_income: number; // After depreciation
  tax_savings: number; // Tax benefit from deductions
  effective_tax_rate: number;
  after_tax_cash_flow: number;
  depreciation_recapture_liability: number;
  tax_profile_used: UserTaxProfile;
}

// Schedule E Helper Types
export interface ScheduleEData {
  property_id: string;
  property_address: string;
  year: string;
  // Income Section
  rental_income: number;
  // Expense Categories (Schedule E Lines)
  advertising: number;
  auto_travel: number;
  cleaning_maintenance: number;
  commissions: number;
  insurance: number;
  legal_professional: number;
  management_fees: number;
  mortgage_interest: number;
  other_interest: number;
  repairs: number;
  supplies: number;
  taxes: number;
  utilities: number;
  depreciation: number;
  other_expenses: OtherExpense[];
  // Calculations
  total_expenses: number;
  net_rental_income: number;
}

// 1031 Exchange Analysis
export interface Exchange1031Analysis {
  property_id: string;
  current_market_value: number;
  adjusted_basis: number; // Original cost minus depreciation
  potential_capital_gain: number;
  depreciation_recapture: number;
  federal_capital_gains_rate: number;
  state_capital_gains_rate: number;
  net_investment_income_tax: number; // 3.8% for high earners
  total_tax_liability: number;
  tax_deferred_via_1031: number;
  exchange_requirements: Exchange1031Requirements;
  like_kind_properties: LikeKindProperty[];
}

export interface Exchange1031Requirements {
  identification_period_days: number; // Typically 45 days
  exchange_period_days: number; // Typically 180 days
  qualified_intermediary_required: boolean;
  like_kind_requirement: string;
  equal_or_greater_value: boolean;
  debt_replacement_rule: string;
}

export interface LikeKindProperty {
  address: string;
  market_value: number;
  property_type: string;
  qualification_status: 'qualified' | 'not_qualified' | 'pending_review';
  qualification_notes: string;
}

// Tax Document Generation
export interface TaxDocumentPackage {
  property_id: string;
  year: string;
  generated_at: string;
  documents: TaxDocument[];
  summary: TaxYearSummary;
}

export interface TaxDocument {
  document_type: 'schedule_e' | 'depreciation_schedule' | 'expense_report' | '1031_worksheet';
  file_name: string;
  file_format: 'pdf' | 'csv' | 'xlsx' | 'turbo_tax' | 'tax_act';
  content: any; // Actual document content/data
  generated_at: string;
}

export interface TaxYearSummary {
  total_rental_income: number;
  total_deductions: number;
  net_rental_income: number;
  total_depreciation: number;
  tax_savings_estimate: number;
  after_tax_cash_flow: number;
  key_deduction_categories: {
    category: string;
    amount: number;
    percentage_of_total: number;
  }[];
}

// Tax Scenario Modeling
export interface TaxScenario {
  scenario_id: string;
  scenario_name: string;
  property_id: string;
  tax_profile_adjustments: Partial<UserTaxProfile>;
  holding_period_years: number;
  annual_rent_growth: number;
  annual_expense_growth: number;
  exit_strategy: 'sale' | '1031_exchange' | 'hold_indefinitely';
  projected_results: TaxScenarioResult[];
}

export interface TaxScenarioResult {
  year: number;
  rental_income: number;
  total_deductions: number;
  depreciation: number;
  taxable_income: number;
  tax_liability: number;
  after_tax_cash_flow: number;
  cumulative_tax_savings: number;
  property_value_estimate: number;
  cumulative_depreciation: number;
}

// Tax Optimization Recommendations
export interface TaxOptimizationRecommendation {
  property_id: string;
  recommendation_type: 'timing' | 'deduction' | 'structure' | 'exchange';
  priority: 'high' | 'medium' | 'low';
  title: string;
  description: string;
  estimated_tax_savings: number;
  implementation_difficulty: 'easy' | 'moderate' | 'complex';
  deadline?: string; // For time-sensitive recommendations
  professional_consultation_recommended: boolean;
}

// API Response Types
export interface TaxAnalysisResponse {
  success: boolean;
  data: TaxAnalysisResult;
  recommendations?: TaxOptimizationRecommendation[];
  error?: string;
}

export interface DepreciationCalculationRequest {
  property_id: string;
  purchase_price: number;
  land_value: number;
  placed_in_service_date: string;
  property_type: 'residential' | 'commercial';
  depreciation_method: 'straight_line' | 'accelerated';
  bonus_depreciation_election?: boolean;
}

export interface TaxProfileUpdateRequest {
  user_id: string;
  profile_updates: Partial<UserTaxProfile>;
}

// Chart Data Types for Visualizations
export interface DepreciationChartData {
  year: string;
  annual_depreciation: number;
  accumulated_depreciation: number;
  remaining_basis: number;
  tax_savings: number;
}

export interface TaxImpactChartData {
  year: string;
  before_tax_cash_flow: number;
  after_tax_cash_flow: number;
  tax_savings: number;
  effective_tax_rate: number;
}

// Form State Management
export interface TaxProfileFormState {
  filing_status: UserTaxProfile['filing_status'] | '';
  federal_tax_bracket: string;
  state: string;
  state_tax_rate: string;
  depreciation_method: UserTaxProfile['depreciation_method'] | '';
  material_participation: boolean;
  agi_estimate: string;
  errors: Record<string, string>;
  isSubmitting: boolean;
}

export interface ScheduleEFormState {
  property_id: string;
  year: string;
  rental_income: string;
  expenses: Record<string, string>;
  other_expenses: OtherExpense[];
  errors: Record<string, string>;
  isSubmitting: boolean;
}

// Constants and Enums
export const TAX_BRACKETS_2024 = {
  single: [
    { min: 0, max: 11600, rate: 0.10 },
    { min: 11600, max: 47150, rate: 0.12 },
    { min: 47150, max: 100525, rate: 0.22 },
    { min: 100525, max: 191950, rate: 0.24 },
    { min: 191950, max: 243725, rate: 0.32 },
    { min: 243725, max: 609350, rate: 0.35 },
    { min: 609350, max: Infinity, rate: 0.37 }
  ],
  married_joint: [
    { min: 0, max: 23200, rate: 0.10 },
    { min: 23200, max: 94300, rate: 0.12 },
    { min: 94300, max: 201050, rate: 0.22 },
    { min: 201050, max: 383900, rate: 0.24 },
    { min: 383900, max: 487450, rate: 0.32 },
    { min: 487450, max: 731200, rate: 0.35 },
    { min: 731200, max: Infinity, rate: 0.37 }
  ]
};

export const DEPRECIATION_PERIODS = {
  residential_rental: 27.5,
  commercial: 39,
  equipment: 5,
  improvements: 15
};

export const SCHEDULE_E_EXPENSE_CATEGORIES = [
  'advertising',
  'auto_travel',
  'cleaning_maintenance',
  'commissions',
  'insurance',
  'legal_professional',
  'management_fees',
  'mortgage_interest',
  'other_interest',
  'repairs',
  'supplies',
  'taxes',
  'utilities',
  'depreciation'
] as const;

export type ScheduleEExpenseCategory = typeof SCHEDULE_E_EXPENSE_CATEGORIES[number];